/**
 * Safe localStorage utility for isomorphic environments
 * Works in browsers, gracefully handles Node.js (no-op)
 */

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
      // Test that localStorage actually works (some browsers block it)
      const testKey = '__agentai_storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

const storageAvailable = isLocalStorageAvailable();

/**
 * Safely set an item in localStorage
 * No-op in Node.js or when localStorage is unavailable
 * 
 * @param key - Storage key
 * @param value - Value to store (will be JSON stringified if object)
 * @returns true if stored successfully, false otherwise
 */
export function setStorageItem(key: string, value: unknown): boolean {
  if (!storageAvailable) {
    return false;
  }

  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    window.localStorage.setItem(key, serialized);
    return true;
  } catch {
    // Storage might be full or blocked
    return false;
  }
}

/**
 * Safely get an item from localStorage
 * Returns null in Node.js or when localStorage is unavailable
 * 
 * @param key - Storage key
 * @param parse - If true, attempts to JSON.parse the value (default: false)
 * @returns The stored value, parsed value, or null
 */
export function getStorageItem<T = string>(key: string, parse: boolean = false): T | null {
  if (!storageAvailable) {
    return null;
  }

  try {
    const value = window.localStorage.getItem(key);
    if (value === null) {
      return null;
    }
    return parse ? JSON.parse(value) : value as T;
  } catch {
    return null;
  }
}

/**
 * Safely remove an item from localStorage
 * No-op in Node.js or when localStorage is unavailable
 * 
 * @param key - Storage key
 * @returns true if removed successfully, false otherwise
 */
export function removeStorageItem(key: string): boolean {
  if (!storageAvailable) {
    return false;
  }

  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely clear all items from localStorage
 * No-op in Node.js or when localStorage is unavailable
 * 
 * @returns true if cleared successfully, false otherwise
 */
export function clearStorage(): boolean {
  if (!storageAvailable) {
    return false;
  }

  try {
    window.localStorage.clear();
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if running in a browser environment with localStorage
 * 
 * @returns true if localStorage is available
 */
export function hasStorage(): boolean {
  return storageAvailable;
}

